import {Box, Typography} from '@mui/material';
import {useGetAllUsersByBoardId} from 'hooks/useUserApi';
import {IdValueModel} from 'models';
import Quill, {QuillOptions} from 'quill';
import {Mention, MentionBlot} from 'quill-mention';
import 'quill-mention/dist/quill.mention.css';
import {forwardRef, useEffect, useMemo, useState} from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';
import {ReactQuillProps} from './ReactQuillProps';
// @ts-ignore
import ImageResize from 'quill-image-resize-module-react';
Quill.register('modules/mention', Mention);
Quill.register('blots/mention', MentionBlot);
Quill.register('modules/imageResize', ImageResize);
export interface TextQuillEditorProps extends ReactQuillProps {
    value: string;
    onChange?: (content: string) => void;
    label?: string;
    required?: boolean;
    scopeId?: string;
}
// Get the base Image blot from Quill

const ImageBlot = Quill.import('formats/image') as any;

class CustomImage extends ImageBlot {
    static create(value: string) {
        const node: HTMLImageElement = super.create(value);

        if (typeof value === 'string') {
            node.setAttribute('src', value);
        }

        // Only apply width if not resized by the module
        if (!node.getAttribute('width')) {
            node.style.maxWidth = 'calc(100% - 150px)';
            node.style.height = 'auto';
            node.style.display = 'block';
            node.style.margin = '10px 0';
        }

        return node;
    }
}

Quill.register('formats/image', CustomImage, true);

const FORMATS = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'indent',
    'align',
    'color',
    'background',
    'link',
    'image',
    'code-block',
    'mention',
];

export const TextQuillEditor = forwardRef<ReactQuill, TextQuillEditorProps>(
    (
        {value = '', onChange, label, required = false, placeholder = 'Compose your content...', scopeId, ...rest},
        ref,
    ) => {
        const handleChange = (content: string) => {
            // Treat <p><br></p> and similar as empty
            const isEmpty = !content || content === '<p><br></p>' || content.replace(/<[^>]+>/g, '').trim() === '';
            onChange?.(isEmpty ? '' : content);
        };
        const {userId} = useSelector(sessionSelector);
        const [mentionList, setMentionList] = useState<IdValueModel[]>([]);
        const {getAllUsers, allUsersByBoardIdLoading, allUsersByBoardIdResponse} = useGetAllUsersByBoardId();

        useEffect(() => {
            if (scopeId) {
                getAllUsers({
                    variables: {
                        filter: {boardId: scopeId},
                    },
                });
            }
        }, [scopeId, getAllUsers]);

        useEffect(() => {
            if (!allUsersByBoardIdLoading && allUsersByBoardIdResponse) {
                const users = allUsersByBoardIdResponse
                    .filter(x => x.id !== userId)
                    .map((user, index) => ({
                        id: user.id,
                        value: `${user.firstName} ${user.lastName}`,
                    }));
                setMentionList(users);
            }
        }, [allUsersByBoardIdLoading, allUsersByBoardIdResponse]);

        const modules: QuillOptions['modules'] = useMemo(() => {
            const baseToolbar = [
                [{header: [1, 2, false]}],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{list: 'ordered'}, {list: 'bullet'}, {indent: '-1'}, {indent: '+1'}],
                [{align: []}, {color: []}, {background: []}],
                ['link', 'image', 'code-block'],
                ['clean'],
            ];

            const mentionModule = mentionList.length
                ? {
                      mention: {
                          allowedChars: /^[A-Za-z\s]*$/,
                          triggerChar: ['@'],
                          source(searchTerm: string, renderList: (data: IdValueModel[], searchTerm: string) => void) {
                              const matches = !searchTerm
                                  ? mentionList
                                  : mentionList.filter(user =>
                                        user.value.toLowerCase().includes(searchTerm.toLowerCase()),
                                    );
                              renderList(matches, searchTerm);
                          },
                          onSelect(item: IdValueModel, insertItem: (item: IdValueModel) => void) {
                              insertItem(item);
                          },
                          mentionDenotationChars: ['@'],
                          fixMentionsToQuill: false,
                          positioningStrategy: 'fixed',
                          offsetTop: 8,
                          offsetLeft: 0,
                          minChars: 0,
                          defaultMenuOrientation: 'bottom',
                          mentionContainerClass: 'custom-mention-container',
                      },
                  }
                : {};

            return {
                toolbar: baseToolbar,
                imageResize: {
                    parchment: Quill.import('parchment'),
                    modules: ['Resize', 'DisplaySize'],
                },
                ...mentionModule,
            };
        }, [mentionList]);

        return (
            <Box
                display="flex"
                flexDirection="column"
                width="100%"
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {label && (
                    <Typography variant="body2" style={{marginBottom: '5px'}}>
                        {label} {required && <span style={{color: 'red'}}>*</span>}
                    </Typography>
                )}
                <ReactQuill
                    theme="snow"
                    value={value}
                    ref={ref}
                    onChange={handleChange}
                    modules={modules}
                    formats={FORMATS}
                    placeholder={placeholder}
                    className="custom-quill [&_.ql-editor]:min-h-[200px]"
                    aria-label="Rich text editor"
                    {...rest}
                />
            </Box>
        );
    },
);

export default TextQuillEditor;
